import { FileText, Search, ShieldCheck } from "lucide-react";
import { Fragment, useState } from "react";
import { toast } from "sonner";
import { toolkitApi } from "@/api/toolkit";
import { Alert, Button, Card, EmptyState, Input } from "@/components/common";
import { useKnowledgeSearch, useSearchStatus } from "@/hooks/useToolkit";
import type { KnowledgeHit, KnowledgeSearchResult } from "@/types/toolkit";

const MAX_LENGTH = 500;

async function openSource(hit: KnowledgeHit) {
  try {
    const blob =
      hit.sourceKind === "owu"
        ? await toolkitApi.downloadOwu(hit.sourceId)
        : await toolkitApi.downloadDocument(hit.sourceId);
    const url = URL.createObjectURL(blob);
    // #page= opens the PDF at the matching page in the browser's viewer.
    window.open(
      hit.page ? `${url}#page=${hit.page}` : url,
      "_blank",
      "noopener",
    );
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 60_000);
  } catch (error) {
    toast.error(
      (error as { message?: string }).message ??
        "Nie udało się otworzyć dokumentu",
    );
  }
}

function escapeRegExp(text: string) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Bolds the words starting with a searched stem ("kradzi" → "kradzież"). */
function Highlighted({ text, terms }: { text: string; terms: string[] }) {
  if (terms.length === 0) {
    return <>{text}</>;
  }
  const pattern = new RegExp(
    `(${terms.map((t) => `${escapeRegExp(t)}[\\p{L}\\p{N}]*`).join("|")})`,
    "giu",
  );
  return (
    <>
      {text.split(pattern).map((part, i) =>
        i % 2 === 1 ? (
          <mark
            // biome-ignore lint/suspicious/noArrayIndexKey: parts of one fixed string
            key={i}
            className="rounded bg-amber-100 px-0.5 text-main dark:bg-amber-500/30"
          >
            {part}
          </mark>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: parts of one fixed string
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

function Hit({ hit, terms }: { hit: KnowledgeHit; terms: string[] }) {
  const where = [hit.versionLabel, hit.page ? `str. ${hit.page}` : null]
    .filter(Boolean)
    .join(" · ");
  return (
    <li className="space-y-1 px-4 py-3">
      <button
        type="button"
        onClick={() => {
          void openSource(hit);
        }}
        className="inline-flex items-start gap-1.5 text-left text-sm font-medium text-adara-700 hover:underline dark:text-adara-300"
      >
        <FileText size={14} aria-hidden className="mt-0.5 shrink-0" />
        {hit.title}
      </button>
      {where && <p className="text-xs text-dim">{where}</p>}
      <p className="text-sm text-sub">
        <Highlighted text={hit.snippet} terms={terms} />
      </p>
    </li>
  );
}

export function SearchTab() {
  const { data: status } = useSearchStatus();
  const search = useKnowledgeSearch();
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState<{
    question: string;
    data: KnowledgeSearchResult;
  } | null>(null);
  const trimmed = question.trim();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (trimmed.length < 3 || search.isPending) {
      return;
    }
    search.mutate(trimmed, {
      onSuccess: (data) => {
        setResult({ question: trimmed, data });
      },
    });
  }

  return (
    <div className="space-y-4">
      <Card>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <Input
              label="Czego szukasz?"
              value={question}
              onChange={(e) => {
                setQuestion(e.target.value);
              }}
              placeholder="np. czy AC obejmuje kradzież pojazdu z garażu"
              leftIcon={<Search size={16} aria-hidden />}
              maxLength={MAX_LENGTH}
              autoComplete="off"
              className="flex-1"
            />
            <Button
              type="submit"
              disabled={trimmed.length < 3}
              isLoading={search.isPending}
            >
              Szukaj
            </Button>
          </div>
          {search.error && (
            <Alert variant="error">
              {(search.error as { message?: string }).message ??
                "Nie udało się wyszukać"}
            </Alert>
          )}
          <p className="inline-flex items-center gap-1.5 text-xs text-dim">
            <ShieldCheck size={14} aria-hidden className="shrink-0" />
            Szuka w {status?.indexedDocuments ?? "…"} dokumentach Podręcznika i
            aktualnych OWU. Działa w całości w panelu — pytanie nie trafia do
            żadnej usługi zewnętrznej.
          </p>
        </form>
      </Card>

      {result &&
        (result.data.hits.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Search size={24} aria-hidden />}
              title="Nic nie znaleziono"
              description={`Dokumenty w Podręczniku i biblioteka OWU nie zawierają fragmentów pasujących do „${result.question}”. Spróbuj innych słów albo zapytaj opiekuna w towarzystwie.`}
            />
          </Card>
        ) : (
          <section className="space-y-2">
            <h2 className="text-xs font-semibold text-dim">
              Najlepiej pasujące fragmenty
            </h2>
            <Card padding="none">
              <ul className="divide-y divide-edge-soft">
                {result.data.hits.map((hit) => (
                  <Hit
                    key={`${hit.sourceId}-${hit.page}-${hit.snippet.slice(0, 20)}`}
                    hit={hit}
                    terms={result.data.terms}
                  />
                ))}
              </ul>
            </Card>
            <p className="text-xs text-faint">
              Kliknij tytuł, aby otworzyć dokument na wskazanej stronie. Zanim
              przekażesz informację klientowi, przeczytaj cały zapis w OWU.
            </p>
          </section>
        ))}
    </div>
  );
}
